/**
 * Device-held end-to-end encryption, shared by the steps that need it
 * (Step 4's inventory, Step 8's amends list — AUTHORIZATION_SPEC §Step 4,
 * §Step 8).
 *
 * Rows are encrypted HERE, on the device, with an AES-GCM 256 key generated on
 * first use and kept in localStorage. The key never leaves the device and is
 * never sent to the server — the API stores and returns ciphertext only.
 *
 * Key-recovery stance is ACCEPT-LOSS (owner decision, Step 4 PRD §8.2): there
 * is no recovery phrase. If the key is lost (new device, cleared storage), the
 * writing is unrecoverable, and each step's storage screen says so plainly.
 *
 * Each surface gets its OWN key, so their lifecycles stay independent: Step 4's
 * writing is usually deleted right after Step 5, while Step 8's list has to
 * survive into Step 9.
 */

function toB64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export type DeviceCipher = {
  /** True if a key already exists on this device (i.e. rows written here). */
  hasKey: () => boolean;
  /** Encrypt a row's fields → opaque base64 (iv ‖ ciphertext). */
  encryptRow: (fields: Record<string, string>) => Promise<string>;
  /** Decrypt a row, or null when this device doesn't hold the right key. */
  decryptRow: (ciphertext: string) => Promise<Record<string, string> | null>;
};

/** One cipher per localStorage key — see the note on independent lifecycles. */
export function createDeviceCipher(storageKey: string): DeviceCipher {
  let cached: CryptoKey | null = null;

  async function getKey(): Promise<CryptoKey> {
    if (cached) return cached;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      cached = await crypto.subtle.importKey(
        "raw",
        fromB64(stored) as unknown as BufferSource,
        { name: "AES-GCM" },
        true,
        ["encrypt", "decrypt"]
      );
      return cached;
    }
    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
      "encrypt",
      "decrypt",
    ]);
    localStorage.setItem(storageKey, toB64(await crypto.subtle.exportKey("raw", key)));
    cached = key;
    return key;
  }

  return {
    hasKey: () => localStorage.getItem(storageKey) !== null,

    async encryptRow(fields) {
      const key = await getKey();
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const plaintext = new TextEncoder().encode(JSON.stringify(fields));
      const ct = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv as unknown as BufferSource },
        key,
        plaintext as unknown as BufferSource
      );
      const out = new Uint8Array(iv.length + ct.byteLength);
      out.set(iv, 0);
      out.set(new Uint8Array(ct), iv.length);
      return toB64(out);
    },

    async decryptRow(ciphertext) {
      try {
        const key = await getKey();
        const data = fromB64(ciphertext);
        const iv = data.slice(0, 12);
        const ct = data.slice(12);
        const plain = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv: iv as unknown as BufferSource },
          key,
          ct as unknown as BufferSource
        );
        return JSON.parse(new TextDecoder().decode(plain)) as Record<string, string>;
      } catch {
        return null;
      }
    },
  };
}
